import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data will be considered stale after 5 minutes
      staleTime: 5 * 60 * 1000,
      // Cache data for 10 minutes
      gcTime: 10 * 60 * 1000,
      // Retry failed queries once
      retry: 1,
      // Don't refetch on window focus for better UX
      refetchOnWindowFocus: false,
      // Don't refetch on reconnect unless data is stale
      refetchOnReconnect: 'always',
    },
    mutations: {
      // Retry failed mutations once
      retry: 1,
    },
  },
});

// Query keys for consistent caching
export const queryKeys = {
  // Auth
  auth: {
    _def: ['auth'] as const,
    currentUser: () => ['auth', 'user'] as const,
  },
  
  // Services
  services: {
    _def: ['services'] as const,
    lists: () => ['services', 'list'] as const,
    list: (params?: any) => ['services', 'list', params] as const,
    detail: (id: number) => ['services', id] as const,
    overview: () => ['services', 'overview'] as const,
  },
  
  // Orders
  orders: {
    _def: ['orders'] as const,
    lists: () => ['orders', 'list'] as const,
    list: (params?: any) => ['orders', 'list', params] as const,
    detail: (id: number) => ['orders', id] as const,
    status: (id: number) => ['orders', id, 'status'] as const,
  },
  
  // Billing
  billing: {
    _def: ['billing'] as const,
    overview: () => ['billing', 'overview'] as const,
    invoices: (params?: any) => ['billing', 'invoices', params] as const,
    invoice: (id: number) => ['billing', 'invoices', id] as const,
  },
  
  // Products
  products: {
    _def: ['products'] as const,
    lists: () => ['products', 'list'] as const,
    detail: (id: number) => ['products', id] as const,
  },
  
  // Activity
  activity: {
    _def: ['activity'] as const,
    lists: () => ['activity', 'list'] as const,
    list: (skip: number, limit: number) => ['activity', 'list', { skip, limit }] as const,
    audits: (serviceId: number) => ['activity', 'audit', serviceId] as const,
    audit: (serviceId: number, skip: number, limit: number) => ['activity', 'audit', serviceId, { skip, limit }] as const,
  },

  // Settings
  settings: {
    _def: ['settings'] as const,
    current: () => ['settings', 'current'] as const,
  },
  
  // Service Management
  samba: {
    status: (serviceId: number) => ['services', serviceId, 'samba', 'status'] as const,
    shares: (serviceId: number) => ['services', serviceId, 'samba', 'shares'] as const,
    permissions: (serviceId: number, share: string) => ['services', serviceId, 'samba', 'shares', share, 'permissions'] as const,
  },
  vpn: {
    status: (serviceId: number) => ['services', serviceId, 'vpn', 'status'] as const,
    users: (serviceId: number) => ['services', serviceId, 'vpn', 'users'] as const,
    config: (serviceId: number, username: string) => ['services', serviceId, 'vpn', 'users', username, 'config'] as const,
  },
  nfs: {
    mounts: (serviceId: number) => ['services', serviceId, 'nfs', 'mounts'] as const,
  },
  userGroups: {
    users: (serviceId: number) => ['services', serviceId, 'users'] as const,
    groups: (serviceId: number) => ['services', serviceId, 'groups'] as const,
  },
} as const;